import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { firebaseMessagingService } from '../config/firebase';
import { API_URL } from '../services/apiClient';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Completing authentication...');

  useEffect(() => {
    let mounted = true;

    const syncUserWithBackend = async (user: { id: string; email?: string; user_metadata?: { full_name?: string } }) => {
      try {
        setStatus('Syncing your account...');
        
        const response = await fetch(`${API_URL}/auth/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authUid: user.id,
            email: user.email,
            name: user.user_metadata?.full_name || user.email?.split('@')[0]
          })
        });

        const result = await response.json();

        if (!response.ok) {
          // User might not have an invite - this is expected for some flows
          console.warn('User sync warning:', result.error);
          return { success: false, error: result.error, user: null };
        }

        return { success: true, user: result.user, error: null };
      } catch (err) {
        console.error('Failed to sync user with backend:', err);
        return { success: false, error: 'Failed to sync account', user: null };
      }
    };

    const handleAuthCallback = async () => {
      try {
        if (!supabase) {
          setError('Supabase is not configured.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        const { data, error } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (error) {
          console.error('Auth callback error:', error);
          setError('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        if (data.session?.user) {
          sessionStorage.setItem('authToken', data.session.access_token);

          const authUid = data.session.user.id;
          localStorage.setItem('authUid', authUid);

          const syncResult = await syncUserWithBackend(data.session.user);
          
          if (syncResult.success && syncResult.user) {
            localStorage.setItem('userRole', syncResult.user.role);
            localStorage.setItem('userId', syncResult.user.id);
            if (syncResult.user.schoolId) {
              localStorage.setItem('schoolId', syncResult.user.schoolId);
            }
            
            setStatus('Account synced! Redirecting...');

            const role = syncResult.user.role;

            // Notify Electron so it can start socket connection + screen monitoring
            if (window.electronAPI?.setUserRole) {
              window.electronAPI.setUserRole({ role, token: data.session.access_token });
            }

            // Non-student roles skip onboarding, so init FCM here (fire-and-forget).
            // Students get FCM initialized during the onboarding notification step.
            if (role !== 'student') {
              firebaseMessagingService.initializeForUser(authUid).catch(() => {});
            }

            setTimeout(() => {
              if (mounted) {
                if (role === 'super_admin') {
                  navigate('/system-admin');
                } else if (role === 'admin') {
                  navigate('/admin');
                } else if (role === 'teacher') {
                  navigate('/teacher');
                } else {
                  navigate('/onboarding');
                }
              }
            }, 1000);
          } else {
            // No invite found or sync failed - still allow through to onboarding
            // The onboarding flow can handle users without invites
            console.log('User sync incomplete, proceeding to onboarding');
            setStatus('Redirecting to setup...');
            
            setTimeout(() => {
              if (mounted) {
                navigate('/onboarding');
              }
            }, 1000);
          }
        } else {
          setError('No authentication session found.');
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (error) {
        console.error('Auth callback exception:', error);
        if (mounted) {
          setError('An unexpected error occurred during authentication.');
          setTimeout(() => navigate('/login'), 2000);
        }
      } finally {
        if (mounted) {
          setIsProcessing(false);
        }
      }
    };

    const urlParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    if (urlParams.get('access_token') || window.location.hash.includes('access_token')) {
      handleAuthCallback();
    } else {
      // No auth parameters found, redirect to login
      setError('Invalid authentication callback.');
      setTimeout(() => navigate('/login'), 1000);
      setIsProcessing(false);
    }

    return () => {
      mounted = false;
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{status}</p>
            <p className="mt-2 text-sm text-gray-500">Please wait while we set up your account.</p>
          </>
        ) : error ? (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="mt-4 text-red-600 font-medium">Authentication Error</p>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <p className="mt-2 text-xs text-gray-500">Redirecting to login page...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-4 text-green-600 font-medium">Authentication Successful</p>
            <p className="mt-2 text-sm text-gray-600">Redirecting to your dashboard...</p>
          </>
        )}
      </div>
    </div>
  );
}